'use client'

import { useEffect, useRef } from 'react'
import { isByokPlansEnabled } from '@/lib/byokPlansFlag'
import { BarAccessCta } from './BarAccessCta'
import { NeonSign } from './NeonSign'

// The top of /bar: a film made in Martini playing full bleed, out of focus, behind the neon
// sign, a filled "Now open" sign, the tagline and Get started. Compact, so the diagram below
// comes into view sooner.
const AMBER = '#ffb347'
// The Hong Kong hotel bar scene from the Model Showcase project (Seedance 2.5), served from the CDN.
const FILM_ASSET = 'https://cdn.martini.film/v0.2/assets/56c2e1af-e9fb-4c37-ade8-b733311cc3f3'
const FILM = {
  video: `${FILM_ASSET}/56c2e1af-e9fb-4c37-ade8-b733311cc3f3.mp4`,
  poster: `${FILM_ASSET}/thumbs/cf521ea1acbeaaf3d5d5d3d132615bccce9dcb40097b4c8bc103af08bf131fa3/720.webp`,
}

const CSS = `
@keyframes open-hum { 0%, 100% { opacity: 1 } 40% { opacity: 1 } 41% { opacity: .45 } 42% { opacity: 1 } 77% { opacity: .85 } 78% { opacity: 1 } }
.open-hum { animation: open-hum 5.3s steps(1, end) 1.4s infinite; }
@media (prefers-reduced-motion: reduce) { .open-hum { animation: none; } }
`

export function BarEntrance() {
  const video = useRef<HTMLVideoElement>(null)

  // The poster shows first; the film starts once the page has settled (unless motion is reduced).
  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return
    const start = window.setTimeout(() => {
      const v = video.current
      if (!v) return
      v.src = FILM.video
      void v.play().catch(() => {})
    }, 800)
    return () => window.clearTimeout(start)
  }, [])

  return (
    <div className="relative flex flex-col items-center overflow-hidden px-6 pb-10 pt-32 text-center md:px-8 md:pb-12 md:pt-36">
      <style>{CSS}</style>
      <div className="absolute inset-0" aria-hidden>
        <video
          ref={video}
          muted
          loop
          playsInline
          preload="none"
          poster={FILM.poster}
          className="absolute inset-0 h-full w-full scale-110 object-cover"
          style={{ filter: 'blur(10px) brightness(0.36) saturate(1.1)' }}
        />
        <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-[#141414] to-transparent" />
      </div>

      <div className="relative flex flex-col items-center">
        <p
          aria-hidden
          className="open-hum mb-5 rounded-full px-3.5 py-1 text-[12px] font-bold uppercase tracking-[0.22em]"
          style={{
            background: AMBER,
            color: '#2a1600',
            boxShadow: `0 0 14px ${AMBER}, 0 0 36px rgba(255,179,71,0.45)`,
          }}
        >
          {/* Agrees with the Get started slot below, which reads "Available soon" while the plans are off. */}
          {isByokPlansEnabled() ? 'Now open' : 'Opening soon'}
        </p>
        <NeonSign />
        <p className="mt-6 max-w-[520px] text-balance text-[17px] leading-relaxed text-white/75 md:text-[19px]">
          Production infrastructure for AI film studios that build their own tools.
        </p>
        <BarAccessCta
          cta="bar_start_free"
          className="group mt-8 inline-flex h-12 items-center gap-2 rounded-xl bg-white px-6 text-[15px] font-semibold text-black transition-colors hover:bg-white/90"
        />
      </div>
    </div>
  )
}
